/**
 * Lightweight admin check used by tracking/heartbeat to exclude admin users
 * from the live analytics feed. Cached in-memory + sessionStorage so it does
 * not add latency to every event/heartbeat.
 */
import { supabase } from "@/integrations/supabase/client";

const CACHE_KEY = "tk_is_admin_v1";
let memo: boolean | null = null;

export function getCachedIsAdmin(): boolean {
  if (memo !== null) return memo;
  try {
    const v = sessionStorage.getItem(CACHE_KEY);
    if (v === "1") {
      memo = true;
      return true;
    }
    if (v === "0") {
      memo = false;
      return false;
    }
  } catch { /* ignore */ }
  return false;
}

export async function refreshIsAdmin(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      memo = false;
      try { sessionStorage.setItem(CACHE_KEY, "0"); } catch { /* ignore */ }
      return false;
    }
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();
    const isAdmin = !!data;
    memo = isAdmin;
    try { sessionStorage.setItem(CACHE_KEY, isAdmin ? "1" : "0"); } catch { /* ignore */ }
    return isAdmin;
  } catch {
    return memo ?? false;
  }
}

export function clearIsAdminCache() {
  memo = null;
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}
